import {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import mapboxgl from 'mapbox-gl';

import { LayerContext } from 'state/LayerContext';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  addAnalysisSegment,
  selectAnalysisSegments,
  selectAnalysisStatus,
  selectClickedMatrixSegmentIds,
  selectHoveredSegmentIds,
  selectIncludeMtr,
  selectODUIMode,
  setClickedMatrixSegmentIds,
  setHoveredSegmentIds,
  undoAnalysisSegment,
  useGetMtrGeojsonQuery,
  useLazyGetSegmentGeometriesQuery,
} from 'state/originDestinationSlices';
import {
  selectCurrentAnalysisId,
  selectCurrentProjectInfo,
  selectMapCenter,
  selectMapStyle,
  selectMapZoom,
  selectProject,
  selectSegmentId,
  setMapCenter,
  setMapZoom,
  setSelectedSegmentId,
} from 'state/workflowSlice';

import { MapContext } from 'features/map/MapContext';
import { useHoverPulseAnimation } from 'features/map/hooks/useHoverPulseAnimation';
import { useMapResize } from 'features/map/hooks/useMapResize';
import { useProjectBoundary } from 'features/map/hooks/useProjectBoundary';
import { MapSettings } from 'features/map/overlays/MapSettings';
import { OutsideBboxPrompt } from 'features/map/overlays/OutsideBboxPrompt';
import { useZoomPrompt } from 'features/map/useZoomPrompt';
import {
  getAllFeaturesFromGeoJSONSource,
  getInitialCenter,
  getSelectionFromClickEvent,
  initMap,
  isUndo,
  mapPopSelectedSegment,
  redrawBaseSegments,
  updateMapLocation,
} from 'features/map/utils/mapCommon';
import {
  getSegmentLayerDefinition,
  getViewOnlySegmentLayerDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import { ensureRtlLoaded, fitBoundsToBbox } from 'features/map/utils/mapUtils';
import {
  kSegmentLayerId,
  kSegmentSourceId,
  kSelectedSegmentLayerId,
} from 'features/map/utils/map_constants';
import { Feature, LineString } from 'geojson';

import { useCallbackWithErrorHandling } from '../../../app/ErrorHandling';
import {
  ACTIVE_STATUSES,
  MAX_OD_SEGMENTS,
  ODUIMode,
  PROCESS_STATUS,
  TIMELINE_MIN_ZOOM,
  mapboxApiKey,
} from '../../../appConstants';
import { LayerRouting } from '../../../appTypes';
import { getIncrementalName, isCtrlEvent } from '../../common/utils';
import { MapLoadingSpinner } from '../../map/MapLoadingSpinner';
import { FloatingDiv } from '../../spinner/Spinner';
import ODMapTooltip from './ODMapTooltip';
import {
  clearMtrLayer,
  getActiveMatrixOdPair,
  getMtrGeojsonForOdPair,
  redrawMtrLayer,
  redrawSelectedODSegments,
  updateODSegmentHover,
} from './utils';

// mapbogxl style should be imported BEFORE Map.css as it must override a handful of styles
import 'mapbox-gl/dist/mapbox-gl.css';

import 'features/map/Map.css';

import styles from 'features/map/Map.module.css';

mapboxgl.accessToken = mapboxApiKey;

export function ODMap({ extras }: { extras?: ReactNode }) {
  const dispatch = useAppDispatch();
  const { refMap, mapReady, setMapReady, refStyleReady } =
    useContext(MapContext);
  const { layer } = useContext(LayerContext);

  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

  const currentAnalysisId = useAppSelector(selectCurrentAnalysisId);
  const selectedSegmentId = useAppSelector(selectSegmentId);
  const project = useAppSelector(selectProject);
  const currentMapCenter = useAppSelector(selectMapCenter);
  const mapZoom = useAppSelector(selectMapZoom);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const status = useAppSelector(selectAnalysisStatus);
  const uiMode = useAppSelector(selectODUIMode);
  const mapStyle = useAppSelector(selectMapStyle);
  const segments = useAppSelector(selectAnalysisSegments);
  const hoveredSegmentIds = useAppSelector(selectHoveredSegmentIds);
  const clickedMatrixSegmentIds = useAppSelector(selectClickedMatrixSegmentIds);
  const includeMtr = useAppSelector(selectIncludeMtr);

  const zoomPrompt = useZoomPrompt(refMap.current, TIMELINE_MIN_ZOOM);
  const [trigger] = useLazyGetSegmentGeometriesQuery();
  const inViewMode = Boolean(currentAnalysisId && uiMode === ODUIMode.View);
  const inCreateMode = uiMode === ODUIMode.Create;
  const hasHovered = hoveredSegmentIds?.length > 0;
  useHoverPulseAnimation({
    hasData: hasHovered,
    shouldStopAnimation: !hasHovered,
  });

  const refHoveredIds = useRef<string[] | null>(hoveredSegmentIds);
  const refMapContainer = useRef(null); // DOM node reference
  const refRouting = useRef<LayerRouting | undefined>(undefined);
  const refUiMode = useRef(uiMode);
  const refSelection = useRef({
    segment: selectedSegmentId,
    segmentIds: (segments ?? []).map((el) => el.id),
    segmentNames: (segments ?? []).map((s) => s.name?.trim().toLowerCase()),
    currentAnalysisId,
  });

  useMapResize(refMap.current, refMapContainer);
  useProjectBoundary(userProject.project_boundary);
  ensureRtlLoaded();

  const editable = uiMode === ODUIMode.Create;
  const isUiModeRouteSelectable = uiMode === ODUIMode.Create;
  const refEnableSegmentSelection = useRef(isUiModeRouteSelectable);

  const refLayer = useRef(layer);
  useEffect(() => {
    refLayer.current = layer;
  }, [layer]);

  useEffect(() => {
    refHoveredIds.current = hoveredSegmentIds;
    if (!refMap.current || !mapReady) return;
    updateODSegmentHover(refMap.current, hoveredSegmentIds);
  }, [hoveredSegmentIds, mapReady]);

  useEffect(() => {
    if (!inViewMode || !refMap.current || !clickedMatrixSegmentIds?.length)
      return;
    const clickedSegments = getAllFeaturesFromGeoJSONSource(
      refMap.current,
      kSelectedSegmentLayerId,
      clickedMatrixSegmentIds,
    );
    if (clickedSegments.length === 0) return;
    const geometries = clickedSegments
      .map((feature) => feature.geometry)
      .filter(Boolean);
    if (geometries.length === 0) return;
    // make sure both are visible on the map
    fitBoundsToBbox(
      refMap.current,
      { type: 'GeometryCollection', geometries },
      0.2,
    );
  }, [clickedMatrixSegmentIds, inViewMode]);

  useEffect(() => {
    dispatch(setSelectedSegmentId(segments?.[segments.length - 1]?.id));
  }, [segments]);

  useEffect(() => {
    refSelection.current.segmentIds = (segments ?? []).map((el) => el.id);
    refSelection.current.segmentNames = (segments ?? []).map((s) =>
      s.name?.trim().toLowerCase(),
    );
    dispatchAnalysisSegmentsRedraw();
  }, [segments, mapReady]);

  function removeLastSelectedSegmentFromMap(map: mapboxgl.Map) {
    const source = map.getSource(kSelectedSegmentLayerId) as
      | mapboxgl.GeoJSONSource
      | undefined;

    if (!source) return;

    const data = (source as any)._data as GeoJSON.FeatureCollection | undefined;
    const features = data?.features ?? [];

    if (features.length <= 1) {
      mapPopSelectedSegment(map, true);
      return;
    }
    source.setData({
      type: 'FeatureCollection',
      features: features.slice(0, -1),
    });
  }

  function dispatchSelectionRedraw(force = false) {
    if (refMap.current) {
      const shouldDrawSelection = refEnableSegmentSelection.current;
      redrawSelectedODSegments(
        refMap.current,
        mapStyle,
        shouldDrawSelection && refSelection.current.segmentIds,
        force,
        undefined,
        refHoveredIds.current,
      );
    }
  }

  async function fetchMissingSegments(
    missingIds: string[],
  ): Promise<Feature<LineString>[]> {
    if (!refLayer.current) return [];
    try {
      const { data } = await trigger({
        segIds: missingIds,
        layerId: refLayer.current,
      });
      return data;
    } catch (e) {
      console.error('Failed to fetch missing segment geometries', e);
    }
    return [];
  }

  function dispatchAnalysisSegmentsRedraw(
    force = false,
    includeMissingSegments = true,
  ) {
    if (!refMap.current || !refStyleReady.current) return;
    const selectedSegmentIds = refSelection.current.segmentIds ?? [];
    if (refMap.current.getLayer(kSegmentLayerId)) {
      if (selectedSegmentIds.length === 0) {
        refMap.current.setFilter(kSegmentLayerId, null);
      } else {
        refMap.current.setFilter(kSegmentLayerId, [
          '!',
          ['in', ['get', 'segment_id'], ['literal', selectedSegmentIds]],
        ]);
      }
    }
    redrawSelectedODSegments(
      refMap.current,
      mapStyle,
      selectedSegmentIds,
      force,
      inViewMode ? fetchMissingSegments : undefined,
      refHoveredIds.current,
      includeMissingSegments,
    );
  }

  const getSegName = (name: string) => {
    const existingSegNames = refSelection.current.segmentNames ?? [];
    while (existingSegNames.includes(name.toLowerCase())) {
      name = getIncrementalName(name);
    }
    return name;
  };

  const handleSegmentClickCallback = useCallbackWithErrorHandling((event) => {
    // Selection is only available in Create mode
    if (refUiMode.current !== ODUIMode.Create) return;
    if (refHoveredIds.current?.length) {
      refHoveredIds.current = null;
      dispatch(setHoveredSegmentIds(null));
    }

    const selectionSegments = refSelection.current.segmentIds;

    const { segmentId: newSelectedSegmentId, segment } =
      getSelectionFromClickEvent(
        event,
        refMap.current,
        refRouting,
        false,
        isCtrlEvent(event.originalEvent),
        refSelection.current.segment,
        selectionSegments,
        undefined,
      );
    if (newSelectedSegmentId) {
      if ((selectionSegments ?? []).length < MAX_OD_SEGMENTS) {
        const name = getSegName(segment.properties.name || 'Unknown');
        dispatch(addAnalysisSegment({ id: newSelectedSegmentId, name }));
      }
    }
  });

  const handleMapMoveCallback = useCallbackWithErrorHandling(() => {
    const center = {
      lng: refMap.current.getCenter().lng.toFixed(4),
      lat: refMap.current.getCenter().lat.toFixed(4),
    };
    const newZoom = refMap.current.getZoom().toFixed(2);
    dispatch(setMapZoom(newZoom));
    dispatch(setMapCenter(center));
  });

  const handleSourceDataEvent = useCallbackWithErrorHandling(
    (e: mapboxgl.MapSourceDataEvent) => {
      // Given we now rely on the segments being loaded via tiles to render
      // segments we have to listen to new tiles being rendered
      // In case analysys segments are suddenly in view
      if (e.sourceId !== kSegmentSourceId) return;
      const selectedSegmentIds = refSelection.current.segmentIds ?? [];
      if (selectedSegmentIds.length === 0) return;
      dispatchAnalysisSegmentsRedraw();
    },
  );

  useEffect(() => {
    if (!mapReady || !refMap.current) return;
    const map = refMap.current;

    const clearMapHoveredSegment = () => {
      if (!refHoveredIds.current?.length) return;
      refHoveredIds.current = null;
      dispatch(setHoveredSegmentIds(null));
    };

    const handleMouseMoveEvent = (e: mapboxgl.MapMouseEvent) => {
      if (!map.getLayer(kSelectedSegmentLayerId)) {
        clearMapHoveredSegment();
        return;
      }
      const feature = map.queryRenderedFeatures(e.point, {
        layers: [kSelectedSegmentLayerId],
      })?.[0];
      const hoveredSegmentId = feature?.properties?.segment_id ?? null;
      const currentHoveredId = refHoveredIds.current?.[0] ?? null;
      if (hoveredSegmentId === currentHoveredId) return;
      refHoveredIds.current = hoveredSegmentId ? [hoveredSegmentId] : null;
      if (!hoveredSegmentIds?.includes(hoveredSegmentId))
        dispatch(
          setHoveredSegmentIds(hoveredSegmentId ? [hoveredSegmentId] : null),
        );
    };

    map.on('mousemove', handleMouseMoveEvent);
    map.on('mouseleave', clearMapHoveredSegment);
    return () => {
      map.off('mousemove', handleMouseMoveEvent);
      map.off('mouseleave', clearMapHoveredSegment);
      clearMapHoveredSegment();
    };
  }, [mapReady]);

  function handleMapLoadCallback() {
    setMapReady(true);
    refStyleReady.current = true;
  }

  function handleKeyStroke(e) {
    // On Ctrl-z, undo the last route selection action
    if (isUiModeRouteSelectable && isUndo(e) && e.target.nodeName !== 'INPUT') {
      e.preventDefault();
      if (refMap.current) removeLastSelectedSegmentFromMap(refMap.current);
      dispatch(undoAnalysisSegment());
    }
  }

  useEffect(() => {
    // Add Key listener to the entire document to capture ctrl z events
    document.addEventListener('keydown', handleKeyStroke);
    return function cleanup() {
      document.removeEventListener('keydown', handleKeyStroke);
    };
  }, [handleKeyStroke]);

  const handleMapSettingsClickCallback = () => {
    setSettingsOpen(true);
  };

  useEffect(() => {
    // Wipe old map
    if (refMap.current) {
      refMap.current.remove();
      refMap.current = undefined;
      setMapReady(false);
      refStyleReady.current = false;
    }
    const { center, zoom } = getInitialCenter(
      userProject,
      currentMapCenter,
      mapZoom,
    );
    if (center) {
      refStyleReady.current = false;
      initMap(
        refMap,
        refMapContainer,
        center,
        zoom,
        mapStyle,
        true,
        {
          handleMapLoadCallback,
          handleMapMoveCallback,
          handleSegmentClickCallback,
          handleMapSettingsClickCallback,
        },
        dispatch,
        userProject,
        false,
        false,
        false,
      );

      refMap.current.on('sourcedata', handleSourceDataEvent);
    } else {
      console.log(
        'Project defaults not yet loaded, skipping initialisation of map',
      );
    }
  }, [userProject, mapStyle]); // eslint-disable-line react-hooks/exhaustive-deps

  function selectableSegmentsLayerDefinition() {
    return getSegmentLayerDefinition(mapStyle);
  }

  /**
   * Draw clickable and nonclickable segments of map.
   */
  useEffect(
    () => {
      if (!(mapReady && refMap.current && refStyleReady.current && layer)) {
        return;
      }
      const baseLayers = [];
      if (!editable) {
        const segmentsDefinition = {
          ...getSegmentLayerDefinition(mapStyle),
          paint: getViewOnlySegmentLayerDefinition(mapStyle).paint,
        };
        baseLayers.push(segmentsDefinition);
      } else {
        // Display the unselectable segments in a different (nonclickable, dimmer) layer
        baseLayers.push(
          ...[
            selectableSegmentsLayerDefinition(),
            getViewOnlySegmentLayerDefinition(mapStyle),
          ],
        );
      }
      redrawBaseSegments(
        mapReady,
        refMap.current,
        baseLayers,
        `server/od_analysis/tiles/${layer}`,
        TIMELINE_MIN_ZOOM,
        16,
        true,
      );
    },
    // we don't need dependency on selectedSegmentId even though we draw the selected statement
    // because (besides initial render) it only erasing/drawing it is handled by handleSegmentClickCallback
    [mapReady, layer, editable, currentAnalysisId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    refSelection.current.segment = selectedSegmentId;
    refSelection.current.segmentIds = (segments ?? []).map((el) => el.id);
    refSelection.current.segmentNames = (segments ?? []).map((s) =>
      s.name?.trim().toLowerCase(),
    );
    dispatchSelectionRedraw();
  }, [selectedSegmentId, segments]);

  useEffect(() => {
    if (refMap.current) {
      const { center } = getInitialCenter(
        userProject,
        currentMapCenter,
        mapZoom,
      );
      refMap.current.jumpTo({ center });
    }
  }, [project]);

  useEffect(() => {
    refUiMode.current = uiMode;
    refEnableSegmentSelection.current = isUiModeRouteSelectable;
  }, [uiMode, isUiModeRouteSelectable]);

  useEffect(() => {
    updateMapLocation(refMap.current, mapZoom, currentMapCenter);
  }, [currentMapCenter, mapZoom]);

  const resultsInProgress = inViewMode && ACTIVE_STATUSES.includes(status);

  const resultsReady = inViewMode && status === PROCESS_STATUS.DONE;

  const { currentData: mtrGeojson } = useGetMtrGeojsonQuery(
    { project_slug: project, analysis_id: currentAnalysisId },
    {
      skip: !resultsReady || !includeMtr || !project || !currentAnalysisId,
    },
  );

  useEffect(() => {
    dispatch(setHoveredSegmentIds(null));
    dispatch(setClickedMatrixSegmentIds(null));
    if (refMap.current) clearMtrLayer(refMap.current);
  }, [currentAnalysisId]);

  const activeMatrixOdPair = useMemo(
    () => getActiveMatrixOdPair(hoveredSegmentIds, clickedMatrixSegmentIds),
    [hoveredSegmentIds, clickedMatrixSegmentIds],
  );

  const visibleMtrGeojson = useMemo(() => {
    if (!inViewMode || !includeMtr || !activeMatrixOdPair) return undefined;
    const [originId, destId] = activeMatrixOdPair;
    return getMtrGeojsonForOdPair(mtrGeojson, originId, destId);
  }, [activeMatrixOdPair, includeMtr, inViewMode, mtrGeojson]);

  useEffect(() => {
    if (!refMap.current || !mapReady || !refStyleReady.current) return;
    if (inViewMode && visibleMtrGeojson?.features?.length) {
      redrawMtrLayer(refMap.current, mapStyle, visibleMtrGeojson);
    } else {
      clearMtrLayer(refMap.current);
    }
  }, [inViewMode, mapReady, mapStyle, visibleMtrGeojson, currentAnalysisId]);

  const showTooltip = inCreateMode || inViewMode;

  return (
    <div ref={refMapContainer} className={styles.map}>
      <MapLoadingSpinner currentRefMap={refMap} mapReady={mapReady} />
      <MapSettings
        open={settingsOpen}
        handleVisSwitch={(visible) => setSettingsOpen(visible)}
      />
      {resultsReady && zoomPrompt && (
        <FloatingDiv>Zoom in to view segments</FloatingDiv>
      )}
      <OutsideBboxPrompt currentRefMap={refMap} />
      {resultsInProgress && (
        <FloatingDiv>
          Your analysis results will appear here once they&apos;re ready
        </FloatingDiv>
      )}
      <div className={styles.tooltip_stack}>
        {showTooltip && (
          <ODMapTooltip viewOnly={!editable} key={currentAnalysisId} />
        )}
        {extras}
      </div>
    </div>
  );
}
